import { openDB } from "idb";
import type { ActiveSession } from "../shared/types";
import { reconcileWorkoutPointer } from "../shared/engine";
import { chooseRemoteRecord } from './session-cache-policy';

export interface QueuedAction {
  id: string;
  profileId: string;
  path: string;
  method: "POST" | "PATCH" | "DELETE";
  body?: unknown;
  createdAt: string;
  sequence?: number;
  /** Unique to this exact value, even when a coalesced action reuses `id`. */
  mutationToken?: string;
  attempts?: number;
  lastError?: string;
}

export interface FlushResult {
  remaining: number;
  blocked: QueuedAction | null;
  responses: Record<string, unknown>;
}

const profileFlushes = new Map<string, Promise<FlushResult>>();

const dbPromise = openDB("hardnup-offline", 4, {
  upgrade(db, oldVersion, _newVersion, transaction) {
    if (oldVersion < 1) {
      db.createObjectStore("state");
      const queue = db.createObjectStore("queue", { keyPath: "id" });
      queue.createIndex("createdAt", "createdAt");
    } else if (oldVersion < 2) {
      transaction.objectStore("queue").createIndex("createdAt", "createdAt");
    }
    if (oldVersion < 3) {
      const queue = transaction.objectStore("queue");
      if (!queue.indexNames.contains("profileId"))
        queue.createIndex("profileId", "profileId");
      // Preserve unowned legacy records for explicit recovery; never replay them
      // under whichever profile happens to sign in after an upgrade.
      void queue.getAll().then(async records => {
        await transaction.objectStore("state").put(records, "recovery:unowned-queue");
        await queue.clear();
      });
      void transaction.objectStore("state").get("active").then(async active => {
        if (active) await transaction.objectStore("state").put(active,"recovery:unowned-active");
        await transaction.objectStore("state").delete("active");
      });
    }
  },
});

const activeKey = (profileId: string) => `active:${profileId}`;
const recordKey = (profileId: string, id: string) => `session:${profileId}:${id}`;

/** Accept a GET only if no local write occurred since it was requested.
 * Checking the queue and storing the response share one transaction, so a set
 * cannot be durably queued in between and then erased by the stale response.
 */
export async function acceptRemoteSession(profileId:string, remote:ActiveSession, baseline:ActiveSession|null):Promise<ActiveSession> {
  if(remote.profileId&&remote.profileId!==profileId) throw new Error('Workout belongs to another profile.');
  const db=await dbPromise;
  const transaction=db.transaction(['state','queue'],'readwrite');
  const state=transaction.objectStore('state');
  const foreground=await state.get(activeKey(profileId)) as ActiveSession|undefined;
  const current=(await state.get(recordKey(profileId,remote.id)) as ActiveSession|undefined)??(foreground?.id===remote.id?foreground:null);
  const queued=await transaction.objectStore('queue').index('profileId').getAll(profileId) as QueuedAction[];
  const chosen=chooseRemoteRecord(remote,current,baseline,queued.some(action=>action.path.includes(`/${remote.id}/`)));
  await state.put(chosen,recordKey(profileId,remote.id));
  if(foreground?.id===remote.id) {
    if(['partial','completed','abandoned'].includes(chosen.status??'')) await state.delete(activeKey(profileId));
    else await state.put(chosen,activeKey(profileId));
  }
  await transaction.done;
  return chosen;
}

export async function saveSessionRecord(profileId: string, session: ActiveSession) {
  if (session.profileId && session.profileId !== profileId) throw new Error("Workout belongs to another profile.");
  const db = await dbPromise;
  const transaction = db.transaction("state", "readwrite");
  await transaction.store.put(session, recordKey(profileId, session.id));
  const foreground = await transaction.store.get(activeKey(profileId)) as ActiveSession | undefined;
  if (foreground?.id === session.id) {
    if(['partial','completed','abandoned'].includes(session.status??'')) await transaction.store.delete(activeKey(profileId));
    else await transaction.store.put(session, activeKey(profileId));
  }
  await transaction.done;
}

export async function hasLegacyRecovery(): Promise<boolean> {
  const db=await dbPromise;
  const queued=await db.get('state','recovery:unowned-queue') as unknown[]|undefined;
  return Boolean(queued?.length || await db.get('state','recovery:unowned-active'));
}

/** An IndexedDB read transaction waits for previously queued workout writes. */
export async function waitForWorkoutWrites() {
  const db=await dbPromise;
  const transaction=db.transaction(['state','queue'],'readonly');
  await transaction.objectStore('state').get('__write_barrier__');
  await transaction.done;
}

export async function getSessionRecord(profileId: string, id: string): Promise<ActiveSession | null> {
  const db = await dbPromise;
  const record = await db.get("state", recordKey(profileId,id)) as ActiveSession | undefined;
  if (record) return record;
  const legacy = await db.get("state", activeKey(profileId)) as ActiveSession | undefined;
  return legacy?.id === id ? legacy : null;
}

export async function localSessionRecords(profileId: string): Promise<ActiveSession[]> {
  const db = await dbPromise;
  const transaction = db.transaction("state");
  const keys = await transaction.store.getAllKeys();
  const records: ActiveSession[] = [];
  for (const key of keys) if (String(key).startsWith(`session:${profileId}:`)) records.push(await transaction.store.get(key));
  const legacy = await transaction.store.get(activeKey(profileId)) as ActiveSession | undefined;
  if (legacy && !records.some(row => row.id === legacy.id)) records.push(legacy);
  return records;
}

export async function sessionHasPending(profileId: string, id: string): Promise<boolean> {
  const actions = await (await dbPromise).getAllFromIndex("queue", "profileId", profileId) as QueuedAction[];
  return actions.some(action => action.path.includes(`/${id}/`));
}

function versioned(action: QueuedAction): QueuedAction {
  return action.mutationToken
    ? action
    : { ...action, mutationToken: crypto.randomUUID() };
}

function isSameMutation(
  current: QueuedAction | undefined,
  snapshot: QueuedAction,
) {
  if (!current) return false;
  if (current.mutationToken || snapshot.mutationToken)
    return current.mutationToken === snapshot.mutationToken;
  // Compatibility for a queue record written by the pre-token release.
  return (
    current.profileId === snapshot.profileId &&
    current.createdAt === snapshot.createdAt &&
    current.sequence === snapshot.sequence &&
    current.path === snapshot.path &&
    current.method === snapshot.method &&
    JSON.stringify(current.body) === JSON.stringify(snapshot.body)
  );
}

export async function saveActiveSession(
  profileId: string,
  session: ActiveSession | null,
) {
  const db = await dbPromise;
  if (session) {
    if (session.profileId && session.profileId !== profileId) throw new Error("Workout belongs to another profile.");
    const transaction = db.transaction("state", "readwrite");
    await transaction.store.put(session, recordKey(profileId, session.id));
    if (session.status === 'partial' || session.status === 'completed' || session.status === 'abandoned') {
      const active=await transaction.store.get(activeKey(profileId)) as ActiveSession|undefined;
      if(active?.id===session.id) await transaction.store.delete(activeKey(profileId));
    }
    else {
      const previous=await transaction.store.get(activeKey(profileId)) as ActiveSession|undefined;
      if(previous && previous.id!==session.id) {
        await transaction.store.put({...previous,status:'partial',pointer:{...previous.pointer,restEndAt:null}},recordKey(profileId,previous.id));
      }
      await transaction.store.put(session, activeKey(profileId));
    }
    await transaction.done;
  }
  else await db.delete("state", activeKey(profileId));
}

export async function getActiveSession(
  profileId: string,
): Promise<ActiveSession | null> {
  const db = await dbPromise;
  const session =
    ((await db.get("state", activeKey(profileId))) as
      | ActiveSession
      | undefined) ?? null;
  if (!session) return null;
  const pointer = reconcileWorkoutPointer(
    session.template,
    session.sets.map((set) => ({
      templateExerciseId: set.templateExerciseId,
      setNumber: set.setNumber,
    })),
    session.pointer,
  );
  if (
    pointer.groupIndex !== session.pointer.groupIndex ||
    pointer.position !== session.pointer.position ||
    pointer.round !== session.pointer.round ||
    pointer.restEndAt !== session.pointer.restEndAt ||
    pointer.selectionRequired !== session.pointer.selectionRequired
  ) {
    const repaired = { ...session, pointer };
    await db.put("state", repaired, activeKey(profileId));
    return repaired;
  }
  return session;
}

export async function enqueue(action: QueuedAction) {
  if (!action.profileId)
    throw new Error("Offline actions require an owning profile.");
  await (await dbPromise).put("queue", versioned(action));
}

export async function persistWorkoutTransition(
  profileId: string,
  session: ActiveSession,
  actions: QueuedAction[],
) {
  if (actions.some((action) => action.profileId !== profileId))
    throw new Error("Offline transition contains a different profile owner.");
  const db = await dbPromise;
  const transaction = db.transaction(["state", "queue"], "readwrite");
  await transaction.objectStore("state").put(session, recordKey(profileId, session.id));
  if (session.status === 'partial') {
    const active=await transaction.objectStore('state').get(activeKey(profileId)) as ActiveSession|undefined;
    if(active?.id===session.id) await transaction.objectStore('state').delete(activeKey(profileId));
  }
  else await transaction.objectStore("state").put(session, activeKey(profileId));
  for (const action of actions)
    await transaction.objectStore("queue").put(versioned(action));
  await transaction.done;
}

export async function persistUndoTransition(
  profileId: string,
  session: ActiveSession,
  removeIds: string[],
  tombstone: QueuedAction,
) {
  if (tombstone.profileId !== profileId)
    throw new Error("Undo tombstone contains a different profile owner.");
  const db = await dbPromise;
  const transaction = db.transaction(["state", "queue"], "readwrite");
  await transaction.objectStore("state").put(session, recordKey(profileId, session.id));
  await transaction.objectStore("state").put(session, activeKey(profileId));
  for (const id of removeIds) {
    const action = (await transaction.objectStore("queue").get(id)) as
      QueuedAction | undefined;
    if (!action || action.profileId === profileId)
      await transaction.objectStore("queue").delete(id);
  }
  await transaction.objectStore("queue").put(versioned(tombstone));
  await transaction.done;
}

export async function removeQueued(profileId: string, id: string) {
  const db = await dbPromise;
  const action = (await db.get("queue", id)) as QueuedAction | undefined;
  if (action?.profileId === profileId) await db.delete("queue", id);
}

export async function queuedCount(profileId: string) {
  return (
    await (await dbPromise).getAllFromIndex("queue", "profileId", profileId)
  ).length;
}

async function flushProfileQueue(profileId: string): Promise<FlushResult> {
  if (!navigator.onLine)
    return {
      remaining: await queuedCount(profileId),
      blocked: null,
      responses: {},
    };
  const db = await dbPromise;
  let blocked: QueuedAction | null = null;
  const responses: Record<string, unknown> = {};
  while (navigator.onLine) {
    const actions = (
      (await db.getAllFromIndex(
        "queue",
        "profileId",
        profileId,
      )) as QueuedAction[]
    ).sort(
      (left, right) =>
        left.createdAt.localeCompare(right.createdAt) ||
        (left.sequence ?? 0) - (right.sequence ?? 0) ||
        left.id.localeCompare(right.id),
    );
    if (!actions.length) break;
    let restartFromCurrentQueue = false;
    for (const action of actions) {
      try {
        const response = await fetch(`/api${action.path}`, {
          method: action.method,
          credentials: "same-origin",
          headers: {
            "X-Hardnup-Profile": profileId,
            ...(action.body ? { "Content-Type": "application/json" } : {}),
          },
          body: action.body ? JSON.stringify(action.body) : undefined,
        });
        if (response.ok) {
          const text = await response.text().catch(() => "");
          let parsed: unknown;
          if (text) {
            try {
              parsed = JSON.parse(text) as unknown;
            } catch {
              parsed = text;
            }
          }
          const transaction = db.transaction(["queue", "state"], "readwrite");
          const queue = transaction.objectStore("queue");
          const current = (await queue.get(action.id)) as
            | QueuedAction
            | undefined;
          const acknowledged = isSameMutation(current, action);
          if (acknowledged) {
            await queue.delete(action.id);
            const sessionMatch = action.path.match(/^\/(?:sessions|records)\/([^/]+)\//);
            if(sessionMatch) {
              const key=recordKey(profileId,sessionMatch[1]);
              const stored=await transaction.objectStore('state').get(key) as ActiveSession|undefined;
              if(stored) {
                const finalKind=action.path.split('/').at(-1);
                const updated={...stored};
                // Lifecycle receipts can precede a newer queued resume/stop.
                // Keep the latest local intent; never replay an old status snapshot.
                if(finalKind==='sets' && action.method==='POST') updated.sets=stored.sets.map(set=>set.clientEventId===action.id?{...set,pending:false}:set);
                await transaction.objectStore('state').put(updated,key);
              }
            }
            if (
              /\/(?:sessions\/[^/]+\/(?:complete|skip-advance)|records\/[^/]+\/(?:stop|close))$/.test(action.path)
            ) {
              const cached = await transaction.objectStore("state").get(activeKey(profileId)) as ActiveSession | undefined;
              const stillQueued = await queue.getAll() as QueuedAction[];
              const laterResume = cached && stillQueued.some(next=>next.profileId===profileId&&next.path===`/records/${cached.id}/resume`);
              if (cached && !laterResume && action.path.includes(`/${cached.id}/`))
                await transaction.objectStore("state").delete(activeKey(profileId));
            }
          }
          await transaction.done;
          if (acknowledged && text) responses[action.id] = parsed;
          if (!acknowledged) {
            // A coalesced action with the same key was written while fetch was
            // in flight. Do not let the stale response delete it; resnapshot so
            // the newer value is sent before any later mutation.
            restartFromCurrentQueue = true;
            break;
          }
          continue;
        }
        const details = await response.text().catch(() => "");
        const failed = {
          ...action,
          attempts: (action.attempts ?? 0) + 1,
          lastError: `${response.status} ${details}`.slice(0, 500),
        };
        const transaction = db.transaction("queue", "readwrite");
        const queue = transaction.objectStore("queue");
        const current = (await queue.get(action.id)) as
          | QueuedAction
          | undefined;
        if (isSameMutation(current, action)) {
          await queue.put(failed);
          blocked = failed;
        } else {
          restartFromCurrentQueue = true;
        }
        await transaction.done;
        break;
      } catch (caught) {
        const failed = {
          ...action,
          attempts: (action.attempts ?? 0) + 1,
          lastError:
            caught instanceof Error ? caught.message : "Network error",
        };
        const transaction = db.transaction("queue", "readwrite");
        const queue = transaction.objectStore("queue");
        const current = (await queue.get(action.id)) as
          | QueuedAction
          | undefined;
        if (isSameMutation(current, action)) {
          await queue.put(failed);
          blocked = failed;
        } else {
          restartFromCurrentQueue = true;
        }
        await transaction.done;
        break;
      }
    }
    if (blocked) break;
    if (restartFromCurrentQueue) continue;
  }
  return { remaining: await queuedCount(profileId), blocked, responses };
}

/**
 * A profile has one ordered writer. This prevents an eager online flush, an
 * `online` event, and a Finish click from racing the same set/pointer stream.
 */
export function flushQueue(profileId: string): Promise<FlushResult> {
  const previous =
    profileFlushes.get(profileId) ??
    Promise.resolve({ remaining: 0, blocked: null, responses: {} });
  const current = previous
    .catch(() => ({ remaining: 0, blocked: null, responses: {} }))
    .then(() => flushProfileQueue(profileId));
  profileFlushes.set(profileId, current);
  void current.finally(() => {
    if (profileFlushes.get(profileId) === current)
      profileFlushes.delete(profileId);
  });
  return current;
}

export function getDeviceId(): string {
  const key = "hardnup-device-id";
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}
